import { useRef, useState } from 'react'
import {Camera, Image, Trash2, X} from 'lucide-react'
import type { Interview, CompetencyScore, InterviewResponse } from '@/hooks/useInterviews'
import type { InterviewTemplate } from '@/hooks/useInterviewTemplates'
import {
  RESULT_LABELS,
  INTERVIEW_PROC_LABELS,
  type InterviewResult,
  type InterviewStatus,
} from '@/lib/recruitment'

interface Props {
  interview: Interview
  template?: InterviewTemplate
  onClose: () => void
  onSave: (data: Partial<Interview>) => void
}

// ─── Captura de foto con cámara ───────────────────────────────────────────────
function CameraCapture({
  onCapture,
  onClose,
}: {
  onCapture: (dataUrl: string) => void
  onClose: () => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [error, setError] = useState('')

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } })
      setStream(s)
      if (videoRef.current) videoRef.current.srcObject = s
    } catch {
      setError('No se pudo acceder a la cámara. Verifica los permisos del navegador.')
    }
  }

  const stopCamera = () => {
    stream?.getTracks().forEach((t) => t.stop())
    setStream(null)
    onClose()
  }

  const capture = () => {
    if (!videoRef.current) return
    const canvas = document.createElement('canvas')
    canvas.width = videoRef.current.videoWidth
    canvas.height = videoRef.current.videoHeight
    canvas.getContext('2d')?.drawImage(videoRef.current, 0, 0)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
    stream?.getTracks().forEach((t) => t.stop())
    onCapture(dataUrl)
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h3 className="font-semibold text-gray-900">Tomar foto del entrevistado</h3>
          <button onClick={stopCamera} className="text-gray-400 hover:text-gray-700">
            <X size={18} />
          </button>
        </div>
        <div className="p-5">
          {error ? (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>
          ) : !stream ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <Camera size={40} className="text-gray-300" />
              <p className="text-sm text-gray-500">Activa la cámara para tomar la foto</p>
              <button
                onClick={startCamera}
                className="rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
              >
                Activar cámara
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full rounded-lg bg-black"
                style={{ maxHeight: 280 }}
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={stopCamera}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={capture}
                  className="rounded-lg bg-amber-400 px-5 py-2 text-sm font-bold text-slate-900 hover:bg-amber-300"
                >
                  <Camera size={15} className="mr-1.5 inline" />
                  Capturar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Modal principal ──────────────────────────────────────────────────────────
function InterviewFormModal({ interview, template, onClose, onSave }: Props) {
  const scaleMax = parseInt((interview.scaleType || template?.scaleType || '10') as string, 10)
  const competencies = template?.competencies || []

  const [status, setStatus] = useState<InterviewStatus>(interview.status || 'programada')
  const [interviewer, setInterviewer] = useState(interview.interviewer || '')
  const [scheduledDate, setScheduledDate] = useState(interview.scheduledDate || '')
  const [observations, setObservations] = useState(interview.observations || '')
  const [strengths, setStrengths] = useState(interview.strengths || '')
  const [opportunities, setOpportunities] = useState(interview.opportunities || '')
  const [recommendations, setRecommendations] = useState(interview.recommendations || '')
  const [privateNotes, setPrivateNotes] = useState(interview.privateNotes || '')
  const [overallScore, setOverallScore] = useState<number>(interview.overallScore ?? 0)
  const [result, setResult] = useState<InterviewResult>(interview.result || 'pendiente')

  // Foto del entrevistado
  const [candidatePhoto, setCandidatePhoto] = useState<string>(
    (interview as Record<string, unknown>)['candidatePhoto'] as string || ''
  )
  const [showCamera, setShowCamera] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Imágenes de Referencias
  const [refImages, setRefImages] = useState<string[]>(
    ((interview as Record<string, unknown>)['referenceImages'] as string[]) || []
  )
  const refInputRef = useRef<HTMLInputElement>(null)

  const [responses, setResponses] = useState<Record<string, string>>(() => {
    const map: Record<string, string> = {}
    ;(interview.responses || []).forEach((r) => (map[r.fieldId] = r.value))
    return map
  })

  const [compScores, setCompScores] = useState<Record<string, number>>(() => {
    const map: Record<string, number> = {}
    ;(interview.competencyScores || []).forEach((c) => (map[c.name] = c.score))
    return map
  })

  // Manejo de foto por archivo
  const handlePhotoFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => setCandidatePhoto(ev.target?.result as string)
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  // Manejo de imágenes de referencia
  const handleRefImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    files.forEach((file) => {
      const reader = new FileReader()
      reader.onload = (ev) => {
        setRefImages((prev) => [...prev, ev.target?.result as string])
      }
      reader.readAsDataURL(file)
    })
    e.target.value = ''
  }

  const removeRefImage = (idx: number) =>
    setRefImages((prev) => prev.filter((_, i) => i !== idx))

  const handleSubmit = () => {
    const responsesArr: InterviewResponse[] = (template?.fields || []).map((f) => ({
      fieldId: f.id,
      label: f.label,
      value: responses[f.id] || '',
    }))
    const competencyScores: CompetencyScore[] = competencies.map((c) => ({
      name: c,
      score: compScores[c] || 0,
    }))
    onSave({
      status,
      interviewer,
      scheduledDate,
      observations,
      strengths,
      opportunities,
      recommendations,
      privateNotes,
      overallScore,
      scaleType: (interview.scaleType || template?.scaleType || '10') as '10' | '100',
      result,
      responses: responsesArr,
      competencyScores,
      // campos extra almacenados como propiedades adicionales
      ...(candidatePhoto ? { candidatePhoto } : {}),
      ...(refImages.length > 0 ? { referenceImages: refImages } : {}),
    } as Partial<Interview>)
  }

  return (
    <>
      {showCamera && (
        <CameraCapture
          onCapture={(dataUrl) => { setCandidatePhoto(dataUrl); setShowCamera(false) }}
          onClose={() => setShowCamera(false)}
        />
      )}

      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
        <div className="my-8 w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <div className="flex items-center gap-3">
              {/* Foto del candidato */}
              <div className="relative flex-shrink-0">
                {candidatePhoto ? (
                  <img
                    src={candidatePhoto}
                    alt="Foto candidato"
                    className="h-12 w-12 rounded-full object-cover border-2 border-amber-300"
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                    <Camera size={20} />
                  </div>
                )}
                {/* Botón foto encima */}
                <button
                  onClick={() => setShowCamera(true)}
                  title="Tomar foto con cámara"
                  className="absolute -bottom-1 -right-1 rounded-full bg-amber-400 p-0.5 text-slate-900 hover:bg-amber-300 shadow"
                >
                  <Camera size={11} />
                </button>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{interview.collaboratorName}</h2>
                <p className="text-xs text-gray-500">
                  {interview.vacancyTitle || 'Vacante'} · {template?.name || 'Sin plantilla'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Subir foto desde archivo */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoFile}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                title="Subir foto desde archivo"
                className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
              >
                <Image size={13} className="mr-1 inline" />
                Subir foto
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-5">
            {/* Cabecera de la entrevista */}
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Entrevistador</label>
                <input
                  value={interviewer}
                  onChange={(e) => setInterviewer(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Fecha</label>
                <input
                  type="datetime-local"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Estado</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as InterviewStatus)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                >
                  {Object.entries(INTERVIEW_PROC_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Respuestas del formulario */}
            {(template?.fields || []).length > 0 && (
              <div className="border-t border-gray-100 pt-4">
                <h3 className="mb-3 font-semibold text-gray-900">Formulario de entrevista</h3>
                <div className="space-y-3">
                  {(template?.fields || []).map((f) => (
                    <div key={f.id}>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        {f.label || 'Pregunta'}{' '}
                        {f.required && <span className="text-red-500">*</span>}
                      </label>
                      {f.type === 'texto_largo' || f.type === 'comentario' ? (
                        <textarea
                          rows={2}
                          value={responses[f.id] || ''}
                          onChange={(e) => setResponses((p) => ({ ...p, [f.id]: e.target.value }))}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                        />
                      ) : f.type === 'casillas' || f.type === 'opcion_multiple' ? (
                        <div className="flex flex-col gap-2">
                          <p className="text-[11px] text-gray-400">
                            Puedes seleccionar una o varias opciones
                          </p>
                          <div className="flex flex-wrap gap-3">
                            {(f.options || []).map((o) => {
                              const selected = (responses[f.id] || '').split('|').filter(Boolean)
                              const checked = selected.includes(o)
                              return (
                                <label key={o} className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 cursor-pointer hover:border-amber-300">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      const next = checked
                                        ? selected.filter((x) => x !== o)
                                        : [...selected, o]
                                      setResponses((p) => ({ ...p, [f.id]: next.join('|') }))
                                    }}
                                    className="rounded border-gray-300"
                                  />
                                  {o}
                                </label>
                              )
                            })}
                          </div>
                        </div>
                      ) : f.type === 'lista' ? (
                        <select
                          value={responses[f.id] || ''}
                          onChange={(e) => setResponses((p) => ({ ...p, [f.id]: e.target.value }))}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                        >
                          <option value="">Seleccionar...</option>
                          {(f.options || []).map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      ) : f.type === 'si_no' ? (
                        <select
                          value={responses[f.id] || ''}
                          onChange={(e) => setResponses((p) => ({ ...p, [f.id]: e.target.value }))}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                        >
                          <option value="">Seleccionar...</option>
                          <option value="Sí">Sí</option>
                          <option value="No">No</option>
                        </select>
                      ) : (
                        <input
                          type={
                            f.type === 'fecha'
                              ? 'date'
                              : f.type === 'numero' || f.type === 'escala'
                              ? 'number'
                              : 'text'
                          }
                          value={responses[f.id] || ''}
                          onChange={(e) => setResponses((p) => ({ ...p, [f.id]: e.target.value }))}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Referencias visuales */}
            <div className="border-t border-gray-100 pt-4">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">Referencias</h3>
                  <p className="text-xs text-gray-400">
                    Imágenes o documentos de referencia para esta entrevista
                  </p>
                </div>
                <button
                  onClick={() => refInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  <Image size={13} /> Agregar imagen
                </button>
                <input
                  ref={refInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleRefImages}
                />
              </div>
              {refImages.length === 0 ? (
                <div
                  onClick={() => refInputRef.current?.click()}
                  className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 py-8 transition hover:border-amber-400 hover:bg-amber-50/30"
                >
                  <Image size={28} className="mb-2 text-gray-300" />
                  <p className="text-xs text-gray-400">Haz clic o arrastra imágenes aquí</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {refImages.map((img, idx) => (
                    <div key={idx} className="group relative aspect-square overflow-hidden rounded-lg border border-gray-200">
                      <img src={img} alt={`Ref ${idx + 1}`} className="h-full w-full object-cover" />
                      <button
                        onClick={() => removeRefImage(idx)}
                        className="absolute right-1 top-1 hidden rounded-full bg-red-500 p-0.5 text-white group-hover:flex"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => refInputRef.current?.click()}
                    className="flex aspect-square items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-amber-400 hover:bg-amber-50/30"
                  >
                    <Image size={20} />
                  </button>
                </div>
              )}
            </div>

            {/* Notas privadas */}
            <div className="border-t border-gray-100 pt-4">
              <h3 className="mb-3 font-semibold text-gray-900">
                Notas de la entrevista{' '}
                <span className="text-xs font-normal text-gray-400">(privadas para la empresa)</span>
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Observaciones</label>
                  <textarea
                    rows={2}
                    value={observations}
                    onChange={(e) => setObservations(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Fortalezas detectadas</label>
                    <textarea
                      rows={2}
                      value={strengths}
                      onChange={(e) => setStrengths(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Áreas de oportunidad</label>
                    <textarea
                      rows={2}
                      value={opportunities}
                      onChange={(e) => setOpportunities(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Recomendaciones</label>
                  <textarea
                    rows={2}
                    value={recommendations}
                    onChange={(e) => setRecommendations(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Comentarios internos</label>
                  <textarea
                    rows={2}
                    value={privateNotes}
                    onChange={(e) => setPrivateNotes(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Evaluación por competencias */}
            {competencies.length > 0 && (
              <div className="border-t border-gray-100 pt-4">
                <h3 className="mb-3 font-semibold text-gray-900">
                  Evaluación por competencias (1 a {scaleMax})
                </h3>
                <div className="space-y-2">
                  {competencies.map((c) => (
                    <div key={c} className="flex items-center gap-3">
                      <span className="flex-1 text-sm text-gray-700">{c}</span>
                      <input
                        type="number"
                        min={0}
                        max={scaleMax}
                        value={compScores[c] ?? ''}
                        onChange={(e) =>
                          setCompScores((p) => ({ ...p, [c]: parseInt(e.target.value, 10) || 0 }))
                        }
                        className="w-20 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-amber-400 focus:outline-none"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Calificación general + resultado */}
            <div className="border-t border-gray-100 pt-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Calificación general (1 a {scaleMax})
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={scaleMax}
                    value={overallScore || ''}
                    onChange={(e) => setOverallScore(parseInt(e.target.value, 10) || 0)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Resultado</label>
                  <select
                    value={result}
                    onChange={(e) => setResult(e.target.value as InterviewResult)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                  >
                    {Object.entries(RESULT_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              className="rounded-lg bg-amber-400 px-5 py-2 text-sm font-bold text-slate-900 hover:bg-amber-300"
            >
              Guardar entrevista
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default InterviewFormModal
